import { createPortal } from 'react-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '@/i18n';
import { applyUpdate, checkForUpdate } from '@/app/appUpdate';

/** Background re-check while the app stays open on one screen. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;
/** Route changes re-check too, but not on every tap. */
const MIN_CHECK_GAP_MS = 30 * 1000;

// Screens where a forced reload would cost the user something: a game round
// in progress (vendor iframe or in-house hash game, both under /game/play),
// or a form half filled in — money, credentials, captcha. A new version found
// here waits, and the prompt appears as soon as the user leaves.
const DEFERRED_ROUTES: RegExp[] = [
  /^\/game\/play\//,
  /^\/withdraw$/,
  /^\/login(\/|$)/,
  /^\/mine\/settings\/(bind-account|change-password|payment-password)$/,
  /^\/mine\/settings\/wallet-address\/(add$|edit\/)/,
  /^\/mine\/settings\/advanced-security\/bind$/,
  /^\/mine\/referral\/team\/create$/,
  /^\/mine\/referral\/team\/[^/]+\/commission$/,
  /^\/mine\/referral\/invitation\/create$/,
];

function isDeferred(pathname: string): boolean {
  return DEFERRED_ROUTES.some((re) => re.test(pathname));
}

/**
 * Forced "new version available" prompt.
 *
 * No close button and no backdrop dismiss: once a newer deploy is detected
 * the only way on is the refresh button. Must sit inside the router (it
 * reads the location), so it is mounted in RouteHost next to the other
 * app-wide popups.
 */
export function AppUpdatePopup() {
  const t = useT('common');
  const { pathname } = useLocation();
  const [updateReady, setUpdateReady] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const foundRef = useRef(false);
  const lastCheckRef = useRef(0);

  // Stable, so the listeners below are attached once. Stops checking for
  // good once an update is found: the prompt is already committed.
  const check = useCallback(() => {
    if (foundRef.current || Date.now() - lastCheckRef.current < MIN_CHECK_GAP_MS) return;
    lastCheckRef.current = Date.now();
    void checkForUpdate().then((ready) => {
      if (!ready) return;
      foundRef.current = true;
      setUpdateReady(true);
    });
  }, []);

  useEffect(() => {
    check();
  }, [pathname, check]);

  useEffect(() => {
    // Coming back to the Mini App after a while is the common way a stale
    // tab meets a new deploy, so check on every return to the foreground.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [check]);

  if (!updateReady || isDeferred(pathname)) return null;

  const onRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    void applyUpdate();
  };

  return createPortal(
    <>
      <div
        aria-hidden
        className="fixed inset-0"
        style={{ zIndex: 10000, backgroundColor: 'rgba(0,0,0,0.5)' }}
      />

      <div
        role="alertdialog"
        aria-modal
        aria-labelledby="app-update-title"
        aria-describedby="app-update-body"
        className="fixed inset-x-0 top-1/2 mx-auto w-[calc(100%-2.5rem)] max-w-[22rem] -translate-y-1/2 overflow-hidden rounded-[1.25rem] bg-white shadow-2xl"
        style={{ zIndex: 10001 }}
      >
        <div className="px-6 pb-6 pt-5">
          <h2
            id="app-update-title"
            className="text-center text-[1rem] font-bold leading-snug text-black"
          >
            {t('app_update_title')}
          </h2>

          <p
            id="app-update-body"
            className="mt-2 text-center text-[0.8125rem] leading-[1.5] text-black/60"
          >
            {t('app_update_body')}
          </p>

          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="mt-5 flex h-11 w-full items-center justify-center rounded-full bg-brand text-[0.875rem] font-semibold text-white disabled:opacity-60"
          >
            {refreshing ? t('app_update_refreshing') : t('app_update_refresh')}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
